import { infiniteQueryOptions, useQueryClient } from '@tanstack/react-query';
import { fetchTournamentPage, useListScope } from '@/hooks/useTournamentList';
import type { TournamentListPage } from '@/hooks/useTournamentList';
import { useTournamentSearchScope } from '@/hooks/useTournamentSearchScope';
import type { TournamentSearchScope } from '@/hooks/useTournamentSearchScope';
import { SEARCH_INDEX_PAGE_SIZE } from '@/constants';

/**
 * Header search tab index: the current tab's list with its filters, no
 * `search`, default sort (the order of the tab list), `SEARCH_INDEX_PAGE_SIZE`
 * per page. The key extends the list key base, so list invalidations refresh
 * it. One builder for the query and its prefetch — the keys never diverge.
 */
export function tournamentSearchIndexOptions(scope: TournamentSearchScope, keyBase: unknown[], ready: boolean) {
  const params = { limit: SEARCH_INDEX_PAGE_SIZE, status: scope.status, season: scope.season, role: scope.role };
  return infiniteQueryOptions({
    queryKey: [...keyBase, 'search', 'index', params],
    queryFn: ({ pageParam, signal }) => fetchTournamentPage(scope.source, { ...params, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last: TournamentListPage) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
    enabled: ready,
  });
}

/** Index query options of `scope` (waits for the user on per-user lists). */
export function useTournamentSearchIndexOptions(scope: TournamentSearchScope) {
  const { keyBase, ready } = useListScope(scope.source);
  return tournamentSearchIndexOptions(scope, keyBase, ready);
}

/**
 * Starts loading the current tab's index ahead of opening search (call it on
 * `pointerdown` of the trigger). Fire-and-forget — never awaits, so the tap's
 * `open()` and focus stay synchronous.
 */
export function usePrefetchTournamentSearchIndex(): () => void {
  const queryClient = useQueryClient();
  const scope = useTournamentSearchScope();
  const { keyBase, ready } = useListScope(scope.source);

  return () => {
    if (!ready) return;
    void queryClient.prefetchInfiniteQuery(tournamentSearchIndexOptions(scope, keyBase, ready));
  };
}
